import {
  MembershipEnrollResponse,
  PatientPortalMembership,
  PatientPortalMembershipDetail,
  PatientPortalMyMembership,
  PaymentMethodConfigResponse,
} from "@/types/patient-portal";
import { PaymentMethodPatient } from "@/types/payment";

import { requestData } from "@/api/api-config";

// ── Payment Methods ─────────────────────────────────────────────────

export async function listPaymentMethods(): Promise<
  PaymentMethodPatient[] | null
> {
  return await requestData<PaymentMethodPatient[]>(
    "patient/patient-portal/payment-methods",
    "get",
  );
}

export async function createPaymentMethodConfig(): Promise<PaymentMethodConfigResponse | null> {
  return await requestData<PaymentMethodConfigResponse>(
    "patient/patient-portal/payment-methods/config",
    "post",
  );
}

export async function savePaymentMethod(
  rainforestPaymentMethodId: string,
  paymentMethodConfigId: string,
): Promise<PaymentMethodPatient | null> {
  return await requestData<PaymentMethodPatient>(
    "patient/patient-portal/payment-methods",
    "post",
    { json: { rainforestPaymentMethodId, paymentMethodConfigId } },
  );
}

export async function setDefaultPaymentMethod(
  paymentMethodId: string,
): Promise<boolean> {
  const result = await requestData<boolean>(
    `patient/patient-portal/payment-methods/${paymentMethodId}/default`,
    "put",
  );
  return result === true;
}

export async function deletePaymentMethod(
  paymentMethodId: string,
): Promise<boolean> {
  // catchError=false so the 409 raised when this card still backs membership
  // billing reaches the caller — swallowing it closed the modal and left the
  // card in the list with no explanation.
  const result = await requestData<boolean>(
    `patient/patient-portal/payment-methods/${paymentMethodId}`,
    "delete",
    undefined,
    false,
  );
  return result === true;
}

// ── Memberships ─────────────────────────────────────────────────────

export async function listAvailableMemberships(): Promise<
  PatientPortalMembership[] | null
> {
  return await requestData<PatientPortalMembership[]>(
    "patient/patient-portal/memberships",
    "get",
  );
}

// Null (for example against an older API) means don't ask.
export async function getMembershipLocationPrompt(): Promise<{
  askForLocation: boolean;
} | null> {
  return await requestData<{ askForLocation: boolean }>(
    "patient/patient-portal/memberships/location-prompt",
    "get",
  );
}

export async function getMembershipDetail(
  membershipId: string,
): Promise<PatientPortalMembershipDetail | null> {
  return await requestData<PatientPortalMembershipDetail>(
    `patient/patient-portal/memberships/${membershipId}`,
    "get",
    undefined,
    false,
  );
}

export async function listMyMemberships(): Promise<
  PatientPortalMyMembership[] | null
> {
  return await requestData<PatientPortalMyMembership[]>(
    "patient/patient-portal/memberships/mine",
    "get",
  );
}

export async function enrollInMembership(
  membershipId: string,
  paymentMethodId: string | null,
  checkoutIdempotencyKey: string,
): Promise<MembershipEnrollResponse | null> {
  return await requestData<MembershipEnrollResponse>(
    `patient/patient-portal/memberships/${membershipId}/enroll`,
    "post",
    { json: { paymentMethodId, checkoutIdempotencyKey } },
    false,
  );
}

export async function updateMembershipPaymentMethod(
  memberId: string,
  paymentMethodId: string,
): Promise<PatientPortalMyMembership | null> {
  // catchError=false so a failure throws instead of resolving to null and
  // toasting a false success — same reasoning as cancelMembership below.
  return await requestData<PatientPortalMyMembership>(
    `patient/patient-portal/memberships/${memberId}/payment-method`,
    "put",
    { json: { paymentMethodId } },
    false,
  );
}

export async function cancelMembership(
  memberId: string,
): Promise<PatientPortalMyMembership | null> {
  // catchError=false so an HTTP error (e.g. 400 when the fee charge is declined /
  // no card on file) throws instead of resolving to null — the caller's catch block
  // surfaces the backend detail rather than showing a false success toast.
  return await requestData<PatientPortalMyMembership>(
    `patient/patient-portal/memberships/${memberId}/cancel`,
    "delete",
    undefined,
    false,
  );
}

/**
 * Fee (in cents) the patient would be charged for cancelling this membership now.
 * Returns null when the request fails (requestData swallows errors), so callers can
 * distinguish an unknown fee from a genuine $0 and warn before the auto-charge.
 */
export async function getEarlyCancellationFee(
  memberId: string,
): Promise<number | null> {
  const result = await requestData<{ feeDue: number }>(
    `patient/patient-portal/memberships/${memberId}/early-cancellation-fee`,
    "get",
  );
  return result ? result.feeDue : null;
}
